import { useEffect, useState, useMemo, useRef } from "react"
import { AppDispatch, RootState } from "@/redux"
import { Separator } from "@/components/ui/separator"
import { useSelector, useDispatch } from "react-redux"
import { FilterCode, FilterOrOptionType } from "@/constants/filters/filters"
import { FetchFilterChoicesRequestBody, FilterResponseV2, FilterSelectOption } from "@/types/WorkflowCreate/filters"
import { InputFieldWrapper } from "../InputFieldsSection"
import { DropdownFilter } from "./DropdownFilter"
import { CheckboxFilter } from "./CheckboxFilter"
import { App } from "@/types/applications"
import { getAvailableFiltersWithChoices } from "@/services/applicationsService"
import { ActionSubjectFilter } from "@/types/WorkflowCreate/AppConfiguration"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { TextboxFilter } from "./TextboxFilter"
import { DateFilter } from "./DateFilter"
import { FieldComparison } from "./FieldComparison"
import { WFCreateStepFilter } from "@/types/WorkflowCreate/workflowCreate"
import { FieldsResponse } from "@/types/WorkflowCreate/fields"

export const StepFilters = ({
  stepId,
  action,
  subject,
  credential,
  order,
  selectedFilters,
  filtersToFetch,
  app,
  isEditing,
  availableFields
}: {
  stepId: string
  action: string,
  subject: string,
  credential: string,
  order: number,
  selectedFilters: WFCreateStepFilter[]
  filtersToFetch?: ActionSubjectFilter[]
  app?: App,
  isEditing?: boolean,
  availableFields?: FieldsResponse
}) => {
  const dispatch: AppDispatch = useDispatch()
  const lastFetchKey = useRef<string | null>(null);
  const { token } = useSelector((state: RootState) => state.user)
  const { lastUpdatedFilter } = useSelector((state: RootState) => state.workflow)
  const [filters, setFilters] = useState<FilterResponseV2[]>([])
  const [isEditingFilters, setIsEditingFilters] = useState(isEditing || false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFiltersToFetch = useMemo(() => filtersToFetch ?? [], [JSON.stringify(filtersToFetch)])
  const stableAppId = useMemo(() => app?.id, [app?.id])
  const stableCredential = useMemo(() => credential, [credential])
  const stableAction = useMemo(() => action, [action])
  const stableSubject = useMemo(() => subject, [subject])
  const dependencyMap = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    stableFiltersToFetch.forEach(filter => {
      filter.dependsOn?.forEach(dep => {
        if (!map[dep.filterCode]) map[dep.filterCode] = new Set();
        map[dep.filterCode].add(filter.code);
      });
    });
    return map;
  }, [stableFiltersToFetch]);
  const prevCore = useRef({ action: stableAction, subject: stableSubject, credential: stableCredential, appId: stableAppId });

  useEffect(() => {
    if (!isEditingFilters || !token || !stableAction || !stableSubject || !stableCredential || !stableAppId || !selectedFilters?.length) return;

    const fetchFiltersWhileEditing = async () => {
      const body: FetchFilterChoicesRequestBody = {
        action: stableAction,
        subject: stableSubject,
        credentialId: stableCredential,
        filters: selectedFilters.map(s => s.filter),
        filterDependencies: selectedFilters.map(s => ({ filterCode: s.filter, value: s.filterValue }))
      }
      const resp = await dispatch(getAvailableFiltersWithChoices(token, stableAppId, body))
      if (resp.data) {
        setFilters(resp.data)
        setIsEditingFilters(false)
      }
    }

    fetchFiltersWhileEditing().catch(console.error)
  }, [isEditingFilters, token, stableAction, stableSubject, stableCredential, stableAppId, selectedFilters, dispatch])

  useEffect(() => {
    const isTriggeringDependency = lastUpdatedFilter && dependencyMap[lastUpdatedFilter];

    // TODO double check for !isEditingFilters or !isEditing
    if (token && stableAppId && stableFiltersToFetch?.length && !isEditingFilters && (isTriggeringDependency || !lastUpdatedFilter)) {
      if (!stableAction || !stableSubject || !stableCredential) return;
      const fetchFilters = async () => {
        const filtersToActuallyFetch = stableFiltersToFetch.filter((filter) => {
          const dependencies = filter.dependsOn;
          // If no dependencies, fetch the filter
          if (!dependencies || dependencies.length === 0) return true;
          // All dependencies must be satisfied
          return dependencies.every(dep => {
            const selected = selectedFilters.find(sf => sf.filter === dep.filterCode);
            // If no selected value, dependency not satisfied
            if (!selected?.filterValue) return false;
            // If filterTrueValues defined, selected value must match one of them
            if (dep.filterTrueValues?.length) {
              return dep.filterTrueValues.includes(selected.filterValue);
            }
            // Just having a value is enough
            return true;
          });
        }).map(f => f.code);

        const body: FetchFilterChoicesRequestBody = {
          action: stableAction,
          subject: stableSubject,
          credentialId: stableCredential,
          filters: filtersToActuallyFetch,
          filterDependencies: selectedFilters.map(s => ({ filterCode: s.filter, value: s.filterValue }))
        }
        if (lastFetchKey.current === JSON.stringify(body)) {
          return; // skip fetch
        }
        lastFetchKey.current = JSON.stringify(body); // Update key

        const resp = await dispatch(getAvailableFiltersWithChoices(
          token,
          stableAppId,
          body
        ))
        if (resp.data) {
          setFilters(resp.data)
          const validFilterCodes = resp.data?.map(f => f.code)
          // Check if any currently selected filter is no longer valid
          const hasInvalid = selectedFilters.some(
            (sf) => !validFilterCodes.includes(sf.filter as FilterCode)
          );
          if (hasInvalid) {
            const cleaned = selectedFilters.filter((sf) =>
              validFilterCodes.includes(sf.filter as FilterCode)
            );
            dispatch(workflowActions.setStepFilters({
              stepId,
              order: order,
              filters: cleaned
            }))
          }
        }
      }
      fetchFilters()
        .catch(err => console.log(err))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch, stableAppId, stableFiltersToFetch, selectedFilters, dependencyMap, lastUpdatedFilter, isEditingFilters, order, stepId])

  useEffect(() => {
    const changed =
      prevCore.current.action !== stableAction ||
      prevCore.current.subject !== stableSubject ||
      prevCore.current.credential !== stableCredential ||
      prevCore.current.appId !== stableAppId;

    if (changed && stableAction && stableSubject && stableCredential && stableAppId) {
      dispatch(workflowActions.setLastUpdatedFilter(undefined));
      lastFetchKey.current = null;
      dispatch(workflowActions.setStepFilters({ stepId, order, filters: [] }));
      setFilters([]);
      prevCore.current = { action: stableAction, subject: stableSubject, credential: stableCredential, appId: stableAppId };
    }
  }, [stableAction, stableSubject, stableCredential, stableAppId, dispatch, stepId, order]);

  const getSelectedFilters = (
    filterCode: string,
  ) => {
    const selectedFilter = selectedFilters.find(fo => fo.filter === filterCode)?.filterValue

    if (!selectedFilter) {
      return undefined
    }

    const filterHasOptions = filters?.find(f => f.code === filterCode)?.values;

    if (!filterHasOptions?.length) {
      return selectedFilter
    }

    const filtersArray = selectedFilter.split(', ');

    const parsedString = filtersArray.map(filterValue => {
      const option = filterHasOptions.find(fv => {
        if (typeof fv === 'number') {
          return String(fv) === filterValue;
        }
        return String(fv.id) === filterValue;
      });

      return typeof option === 'number' ? option : option?.name;
    }).join(', ');

    if (parsedString.length === 0) {
      return selectedFilter
    }

    return parsedString
  };


  const hasSelectedFilter = (filter: FilterResponseV2): boolean => {
    const hasFilter = getSelectedFilters(filter.code)
    return !!hasFilter
  }

  const isSelectOption = (fv: number | FilterSelectOption): fv is FilterSelectOption => {
    return typeof fv === 'object' && fv !== null && 'id' in fv && 'name' in fv
  }

  const getSelectOptions = (
    filter: FilterResponseV2
  ): { id: string; value: string; description?: string }[] => {
    return filter.values.map((fv) => {
      if (isSelectOption(fv)) {
        return {
          id: String(fv.id),
          value: fv.name,
          description: fv.folder ?? fv.description
        }
      }
      return { id: String(fv), value: String(fv) }
    })
  }

  return filters?.length > 0 && (
    <InputFieldWrapper key={app?.id} title="Filters" subtitle='Use these filters to fetch the most relevant data for you.'>
      <div className='my-4' />
      <div className="flex flex-col w-full" >
        {filters.map((filter, i) => {
          const selectedValue = selectedFilters?.find(tf => tf.filter === filter?.code)?.filterValue
          const selectOptions = getSelectOptions(filter)
          return (
            <div key={i + filter.code} className="w-full">
              <div className="flex flex-row items-end justify-between w-full">
                <div className="flex flex-col items-start w-full mr-3">
                  <h3 className="text-primary font-semibold text-base mr-2">
                    {filter.displayName}
                  </h3>
                  {filter.description && <p className="text-secondary text-sm mb-2 text-left">{filter.description}</p>}
                  <div
                    className={`w-full h-10 text-left rounded-md border-input bg-background px-3 py-2 text-sm flex items-center 
                  ${hasSelectedFilter(filter) ? 'text-primary' : 'text-secondary'}`}
                  >
                    <span className="w-full max-w-[440px] truncate">
                      {hasSelectedFilter(filter) ? getSelectedFilters(filter.code) : 'Select'}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={ButtonVariant.OUTLINE}
                  className="mr-3 font-semibold"
                  disabled={!selectedFilters.find(sf => sf.filter === filter.code)?.filterValue}
                  onClick={() => dispatch(workflowActions.removeStepFilter({
                    stepId,
                    order,
                    filter: filter.code
                  }))}
                >
                  Clear
                </Button>
                {filter.type === FilterOrOptionType.DROPDOWN ? (
                  <DropdownFilter
                    stepId={stepId}
                    code={filter.code}
                    order={order}
                    selectOptions={selectOptions}
                    selectedValue={selectedValue}
                    name={filter.displayName}
                    description={filter.description}
                  />
                ) : null}
                {filter.type === FilterOrOptionType.CHECKBOX ? (
                  <CheckboxFilter
                    stepId={stepId}
                    code={filter.code}
                    order={order}
                    selectOptions={selectOptions}
                    selectedValue={selectedValue}
                    name={filter.displayName}
                    description={filter.description}
                  />
                ) : null}
                {filter.type === FilterOrOptionType.TEXTBOX ? (
                  <TextboxFilter
                    stepId={stepId}
                    code={filter.code}
                    order={order}
                    selectOptions={selectOptions}
                    selectedValue={selectedValue}
                    name={filter.displayName}
                    description={filter.description}
                  />
                ) : null}
                {filter.type === FilterOrOptionType.DATE ? (
                  <DateFilter
                    stepId={stepId}
                    code={filter.code}
                    order={order}
                    selectOptions={selectOptions}
                    selectedValue={selectedValue}
                    name={filter.displayName}
                    description={filter.description}
                  />
                ) : null}
                {filter.type === FilterOrOptionType.FIELD_COMPARISON ? (
                  <FieldComparison
                    stepId={stepId}
                    code={filter.code}
                    order={order}
                    selectOptions={selectOptions}
                    selectedValue={selectedValue}
                    name={filter.displayName}
                    description={filter.description}
                    availableFields={availableFields}
                  />
                ) : null}
              </div>
              {i < filters?.length - 1 && <Separator className="my-4" />}
            </div>
          )
        })}
      </div>
    </InputFieldWrapper>
  )
}
